import { Temporal } from "@js-temporal/polyfill";
import type { Game, GameSnapshot, PerformanceDriveSetup } from "../core/Game";
import type { PerformanceSnapshot } from "../core/PerformanceMonitor";
import type { MutableWallClock } from "../time/GameClock";
import { Minimap } from "./Minimap";

interface VoxcityDiagnostics {
  snapshot(): GameSnapshot;
  readonly player: GameSnapshot["player"];
  readonly chunks: string[];
  readonly counts: { active: number; proxy: number; abstract: number };
  readonly weather: GameSnapshot["weather"];
  readonly recentErrors: string[];
  advanceClock(seconds: number): void;
  setClock(value: string): void;
  teleport(x: number, z: number): void;
  preparePerformanceDrive(): PerformanceDriveSetup;
  resetPerformanceMetrics(): void;
  performanceSnapshot(): PerformanceSnapshot;
}

export class GameShell {
  readonly root: HTMLElement;
  readonly hud: HTMLElement;
  readonly title: HTMLElement;
  readonly pauseMenu: HTMLElement;
  readonly dialog: HTMLDialogElement;
  readonly live: HTMLElement;
  readonly minimap: Minimap;
  #hudTimer = 0;
  #vehicleTimer = 0;
  #navigationTimer = 0;
  #lastVehicleEventSequence = 0;
  #playing = false;
  #lastFocus: HTMLElement | null = null;

  constructor(readonly game: Game) {
    const root = document.createElement("div");
    root.className = "game-shell";
    const title = document.createElement("section");
    title.className = "screen title-screen";
    title.setAttribute("aria-label", "Voxcity title");
    title.innerHTML = `<div class="title-panel"><h1 class="wordmark">VOX<span>CITY</span></h1><p class="title-subtitle">New York City, alive. Cross five boroughs, enter the city, and ride its pulse—at your own pace.</p><div class="title-actions"><button class="primary" data-action="play">Play</button><button data-action="fullscreen">Enter Fullscreen</button></div><div class="loading-status"><span>Five boroughs · data-locked snapshot</span><strong>City ready</strong></div></div>`;
    const hud = document.createElement("div");
    hud.className = "hud hidden";
    hud.setAttribute("aria-label", "Game status");
    hud.innerHTML = `<div class="hud-top"><div class="hud-cluster"><div class="hud-block"><div class="hud-label">New York</div><div class="hud-value" data-hud="clock">—</div></div><div class="hud-block"><div class="hud-label">Weather</div><div class="hud-value" data-hud="weather">—</div></div><div class="hud-block"><div class="hud-label">Location</div><div class="hud-value" data-hud="place">Vanderbilt Plaza</div></div><div class="hud-block vehicle-status hidden"><div class="hud-label">Vehicle</div><div class="hud-value" data-hud="vehicle">—</div></div></div><div class="compass" aria-label="Facing north, 0 degrees"><span data-hud="compass-left">NW</span><span class="active" data-hud="compass-heading">N</span><span data-hud="compass-right">NE</span></div></div><div class="interaction"><kbd>E</kbd><span data-hud="interaction">View Grand Central departures</span></div>`;
    const minimap = new Minimap();
    hud.append(minimap.canvas);
    const pauseMenu = document.createElement("section");
    pauseMenu.className = "screen pause-screen hidden";
    pauseMenu.setAttribute("aria-label", "Paused");
    pauseMenu.innerHTML = `<div class="menu"><h2>City paused</h2><p class="context">Vanderbilt Plaza · Grand Central</p><div class="menu-actions"><button class="primary" data-action="resume">Resume</button><button data-action="settings">Settings</button><button data-action="departures">Grand Central departures</button><button data-action="licenses">Data & Licenses</button><button data-action="return">Return to Grand Central</button><button data-action="fullscreen">Enter Fullscreen</button></div><div class="menu-footer"><span>On foot: WASD move · Shift run · Space jump · Driving: W/S accelerate/reverse · A/D steer · E exit</span><span>ESC</span></div></div>`;
    const dialog = document.createElement("dialog");
    const live = document.createElement("div");
    live.className = "live-region";
    live.setAttribute("aria-live", "polite");
    root.append(title, hud, pauseMenu, dialog, live);
    document.body.append(root);
    this.root = root;
    this.hud = hud;
    this.title = title;
    this.pauseMenu = pauseMenu;
    this.dialog = dialog;
    this.live = live;
    this.minimap = minimap;
    root.addEventListener("click", this.#onClick);
    window.addEventListener("keydown", this.#onKey);
    document.addEventListener("fullscreenchange", this.#syncFullscreenLabel);
    this.#hudTimer = window.setInterval(this.#refreshHud, 1000);
    this.#vehicleTimer = window.setInterval(this.#refreshVehicleHud, 250);
    this.#navigationTimer = window.setInterval(this.#refreshNavigation, 100);
    this.#refreshHud();
    this.#refreshVehicleHud();
    this.#refreshNavigation();
  }

  readonly #onClick = (event: MouseEvent): void => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-action]");
    if (!button) return;
    const action = button.dataset.action;
    if (action === "play" || action === "resume") this.play();
    else if (action === "fullscreen") void this.#toggleFullscreen();
    else if (action === "settings") this.#openSettings();
    else if (action === "licenses") this.#openLicenses();
    else if (action === "departures") this.#openDepartures();
    else if (action === "return") {
      this.live.textContent = "Returned to Grand Central";
      this.play();
    } else if (action === "close-dialog") this.dialog.close();
  };

  readonly #onKey = (event: KeyboardEvent): void => {
    if (event.code === "Escape") {
      if (this.dialog.open) return;
      if (this.#playing) this.pause();
      else if (!this.title.classList.contains("hidden")) return;
    }
    if (event.code === "KeyE" && this.#playing && !this.dialog.open) {
      const result = this.game.interact();
      if (result === "departures") this.#openDepartures();
      else if (result === "entered") this.live.textContent = "Entered interior";
      else if (result === "exited") this.live.textContent = "Returned to the street";
      else if (result === "boarded") this.live.textContent = "Transit boarded";
      else if (result === "alighted") this.live.textContent = "Returned to the platform";
      else if (result === "car-entered") this.live.textContent = "Vehicle taken over";
      else if (result === "car-exited") this.live.textContent = "Exited vehicle";
      else if (result === "exit-blocked")
        this.live.textContent = "Exit blocked; move to a clear area and try again";
      else this.live.textContent = "Nothing to interact with nearby";
      this.#refreshHud();
      this.#refreshVehicleHud();
    }
  };

  play(): void {
    this.title.classList.add("hidden");
    this.pauseMenu.classList.add("hidden");
    this.hud.classList.remove("hidden");
    this.#playing = true;
    this.game.play();
    void this.game.canvas.requestPointerLock().catch(() => undefined);
    this.live.textContent = "Exploration resumed";
  }

  pause(): void {
    document.exitPointerLock();
    this.game.pause();
    this.#playing = false;
    this.pauseMenu.classList.remove("hidden");
    this.pauseMenu.querySelector<HTMLButtonElement>('[data-action="resume"]')?.focus();
  }

  #openSettings(): void {
    this.#lastFocus = document.activeElement as HTMLElement;
    this.dialog.innerHTML = `<div class="dialog-body"><div class="dialog-head"><h2>Settings</h2><button data-action="close-dialog" aria-label="Close settings">Close</button></div><label class="settings-row"><span>City detail</span><select data-setting="profile"><option>low</option><option selected>medium</option><option>high</option></select></label><label class="settings-row"><span>Reduced motion and particles</span><input type="checkbox" data-setting="motion" /></label><div class="settings-row"><span>Camera sensitivity</span><input type="range" min="40" max="160" value="100" aria-label="Camera sensitivity" /></div><p class="context">Controls: On foot: WASD move · Shift run · Space jump. Driving: W/S accelerate/reverse · A/D steer · E exit. Mouse orbit · Escape pause.</p></div>`;
    this.dialog.showModal();
    this.dialog
      .querySelector<HTMLSelectElement>('[data-setting="profile"]')
      ?.addEventListener("change", (event) =>
        this.game.setProfile(
          (event.target as HTMLSelectElement).value as "low" | "medium" | "high",
        ),
      );
    this.dialog.addEventListener("close", () => this.#lastFocus?.focus(), { once: true });
  }

  #openDepartures(): void {
    this.#lastFocus = document.activeElement as HTMLElement;
    this.game.pause();
    this.dialog.innerHTML = `<div class="dialog-body"><div class="dialog-head"><div><h2>Grand Central</h2><div class="hud-label">Upcoming departures</div></div><button data-action="close-dialog" aria-label="Close departures">Close</button></div><table><thead><tr><th>Line</th><th>Destination</th><th>Departs</th></tr></thead><tbody><tr><td><span class="route-bullet">4</span>Lexington Av</td><td>Woodlawn</td><td class="arrival">Due</td></tr><tr><td><span class="route-bullet">M42</span>42 Street</td><td>East Side</td><td class="arrival">4 min</td></tr><tr><td><span class="route-bullet">H</span>Harlem</td><td>North White Plains</td><td>12:22</td></tr></tbody></table></div>`;
    this.dialog.showModal();
    this.dialog.addEventListener(
      "close",
      () => {
        this.#lastFocus?.focus();
        if (this.#playing) this.game.play();
      },
      { once: true },
    );
  }

  #openLicenses(): void {
    this.#lastFocus = document.activeElement as HTMLElement;
    this.dialog.innerHTML = `<div class="dialog-body"><div class="dialog-head"><h2>Data & Licenses</h2><button data-action="close-dialog" aria-label="Close licenses">Close</button></div><p><a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">© OpenStreetMap contributors · ODbL 1.0</a></p><p>NYC Open Data supplied by NYC Office of Technology and Innovation and NYC Parks. Data is provided “as is” and may contain errors or omissions.</p><p>The Voxcity world database is an ODbL 1.0 derivative database. Transit timing is synthetic and is not an official schedule.</p></div>`;
    this.dialog.showModal();
    this.dialog.addEventListener("close", () => this.#lastFocus?.focus(), { once: true });
  }

  async #toggleFullscreen(): Promise<void> {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      this.live.textContent = "Fullscreen is unavailable; play continues in the browser window.";
    }
  }

  readonly #syncFullscreenLabel = (): void => {
    for (const button of this.root.querySelectorAll<HTMLButtonElement>(
      '[data-action="fullscreen"]',
    ))
      button.textContent = document.fullscreenElement ? "Exit Fullscreen" : "Enter Fullscreen";
  };

  readonly #refreshHud = (): void => {
    const snapshot = this.game.snapshot();
    const clock = this.hud.querySelector('[data-hud="clock"]');
    const weather = this.hud.querySelector('[data-hud="weather"]');
    const place = this.hud.querySelector('[data-hud="place"]');
    if (clock) clock.textContent = this.game.clock.formatHud();
    if (weather)
      weather.textContent = `${snapshot.weather.category[0]?.toUpperCase()}${snapshot.weather.category.slice(1)} · ${Math.round(snapshot.weather.precipitation * 100)}%`;
    if (place) place.textContent = snapshot.context;
  };

  readonly #refreshVehicleHud = (): void => {
    const snapshot = this.game.snapshot();
    const status = this.hud.querySelector<HTMLElement>(".vehicle-status");
    const value = this.hud.querySelector('[data-hud="vehicle"]');
    const interaction = this.hud.querySelector('[data-hud="interaction"]');
    if (snapshot.driving) {
      status?.classList.remove("hidden");
      if (value)
        value.textContent = `${snapshot.driving.speedKph} km/h · ${snapshot.driving.health}% integrity`;
    } else {
      status?.classList.add("hidden");
    }
    if (interaction) {
      interaction.textContent =
        snapshot.driving?.health === 0
          ? "Exit disabled vehicle"
          : snapshot.driving
            ? "Exit vehicle"
            : snapshot.nearbyCarId !== null
              ? "Take over vehicle"
              : snapshot.context.startsWith("Aboard")
                ? "Alight at the next safe stop"
                : snapshot.context.startsWith("Interior")
                  ? "Exit to East 42nd Street"
                  : snapshot.context.includes("Track level")
                    ? "Board nearby train or exit Grand Central"
                    : snapshot.context.includes("Building entrance")
                      ? "Enter building"
                      : snapshot.context.includes("Grand Central")
                        ? "View Grand Central departures"
                        : "Interact";
    }
    const event = snapshot.vehicleEvent;
    if (!event || event.sequence <= this.#lastVehicleEventSequence) return;
    this.#lastVehicleEventSequence = event.sequence;
    this.live.textContent =
      event.drivenDisabled && event.exitBlocked
        ? "Vehicle disabled. Exit is blocked; press E to retry."
        : event.drivenDisabled && event.otherDisabled
          ? "Crash. Both vehicles disabled; exited safely."
          : event.drivenDisabled
            ? "Vehicle disabled; exited safely."
            : event.otherDisabled
              ? `Crash. Other vehicle disabled. Vehicle integrity ${event.drivenHealth} percent.`
              : `Crash. Vehicle integrity ${event.drivenHealth} percent.`;
  };

  readonly #refreshNavigation = (): void => {
    const snapshot = this.game.navigationSnapshot();
    const points = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"] as const;
    const names = [
      "north",
      "northeast",
      "east",
      "southeast",
      "south",
      "southwest",
      "west",
      "northwest",
    ] as const;
    const index = Math.round(snapshot.headingDegrees / 45) % 8;
    const compass = this.hud.querySelector<HTMLElement>(".compass");
    const left = this.hud.querySelector('[data-hud="compass-left"]');
    const heading = this.hud.querySelector('[data-hud="compass-heading"]');
    const right = this.hud.querySelector('[data-hud="compass-right"]');
    if (left) left.textContent = points[(index + 7) % 8]!;
    if (heading) heading.textContent = points[index]!;
    if (right) right.textContent = points[(index + 1) % 8]!;
    compass?.setAttribute(
      "aria-label",
      `Facing ${names[index]}, ${Math.round(snapshot.headingDegrees)} degrees`,
    );
    this.minimap.render(snapshot);
  };

  dispose(): void {
    clearInterval(this.#hudTimer);
    clearInterval(this.#vehicleTimer);
    clearInterval(this.#navigationTimer);
    this.minimap.dispose();
    this.root.removeEventListener("click", this.#onClick);
    window.removeEventListener("keydown", this.#onKey);
    document.removeEventListener("fullscreenchange", this.#syncFullscreenLabel);
    this.root.remove();
  }
}

export function installDiagnostics(game: Game, clock: MutableWallClock | undefined): void {
  if (import.meta.env.PROD) return;
  Object.defineProperty(window, "__VOXCITY__", {
    configurable: true,
    value: {
      snapshot: () => game.snapshot(),
      get player() {
        return game.snapshot().player;
      },
      get chunks() {
        return game.snapshot().chunks;
      },
      get counts() {
        const value = game.snapshot();
        return {
          active: value.activeCount,
          proxy: value.proxyCount,
          abstract: value.abstractCount,
        };
      },
      get weather() {
        return game.snapshot().weather;
      },
      get recentErrors() {
        return game.snapshot().errors;
      },
      advanceClock(seconds: number) {
        clock?.advance(seconds);
        game.resynchronize();
      },
      setClock(value: string) {
        clock?.set(Temporal.ZonedDateTime.from(value).toInstant());
        game.resynchronize();
      },
      teleport(x: number, z: number) {
        game.repositionPlayer(x, z);
      },
      preparePerformanceDrive: () => game.preparePerformanceDrive(),
      resetPerformanceMetrics: () => game.resetPerformanceMetrics(),
      performanceSnapshot: () => game.performanceSnapshot(),
    },
  });
}

declare global {
  interface Window {
    __VOXCITY__?: VoxcityDiagnostics;
  }
}
